import React from 'react'
import { NavLink } from 'react-router-dom'
import { LayoutDashboard, Layers, MessageSquare, Users, Phone, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { useCallStore } from '@/stores/callStore'
import { MobileDockShell } from '@/components/ui/MobileDockShell'

// Button order per spec: Work, Customers, Dashboard, Inbox, Phone
// Dashboard is index 2 (center of 5) — the More notch is anchored to its position.
const NAV_TABS = [
  { label: 'Work',      path: '/work',      icon: Layers },
  { label: 'Customers', path: '/customers', icon: Users },
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Inbox',     path: '/inbox',     icon: MessageSquare },
] as const

function TabItem({
  icon: Icon,
  label,
  isActive,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  isActive: boolean
}) {
  return (
    <>
      <span
        className={cn(
          'flex items-center justify-center w-11 h-7 rounded-full transition-all duration-200',
          isActive ? 'bg-white/10' : 'bg-transparent',
        )}
      >
        <Icon
          className={cn(
            'w-[18px] h-[18px] transition-colors duration-200',
            isActive ? 'text-white' : 'text-white/40',
          )}
        />
      </span>
      <span
        className={cn(
          'text-[9px] font-semibold leading-none tracking-tight transition-colors duration-200',
          isActive ? 'text-white' : 'text-white/40',
        )}
      >
        {label}
      </span>
    </>
  )
}

// Subtle dome — wide and short so it reads as a gentle indicator, not a peak.
// Tangent-continuous at endpoints and apex.
const HUMP_W = 96
const HUMP_H = 6

/**
 * Tap target for the More dome, kept separate from the dome's own width.
 *
 * The two were the same number, which is how a 96px-wide invisible button
 * ended up floating over page content: the dome is a 96×6 painted arc, but
 * the button inherited that width and added 44px of height above the dock.
 */
const MORE_TAP_W = 44
const MORE_TAP_H = 22
// Fill: closed path so the dome has a solid background
const HUMP_FILL_D = `M 0 ${HUMP_H} C 24 ${HUMP_H} 32 0 48 0 C 64 0 72 ${HUMP_H} ${HUMP_W} ${HUMP_H} Z`
// Border: open path — arc only, no bottom edge (pill border provides that)
const HUMP_BORDER_D = `M 0 ${HUMP_H} C 24 ${HUMP_H} 32 0 48 0 C 64 0 72 ${HUMP_H} ${HUMP_W} ${HUMP_H}`

// Pill surface tokens — single source of truth so the dome and pill match.
const DOCK_BG = 'rgba(10,10,12,0.88)'
const DOCK_BORDER = 'rgba(255,255,255,0.10)'

export function BottomTabBar() {
  const { setMoreMenuOpen, moreMenuOpen } = useUIStore()
  const softphoneExpanded = useCallStore((s) => s.softphoneExpanded)
  const setSoftphoneExpanded = useCallStore((s) => s.setSoftphoneExpanded)

  return (
    // xl:hidden = visible at <1280px, matches FOR-1494 breakpoint.
    // insetTarget="children" — MobileDockShell exposes the safe-area inset as
    // --mobile-dock-safe-bottom; the pill consumes it via paddingBottom so the
    // pill's surface extends through the inset zone, flush with the screen
    // bottom (no visible gap below the dock on iOS).
    // insetTarget="children" — shell exposes the safe-area-inset-bottom value
    // as --mobile-dock-safe-bottom but does NOT pad itself, so we can choose
    // exactly how much of the inset to leave as breathing room below the pill.
    <MobileDockShell
      className="xl:hidden"
      insetTarget="children"
      style={{
        // Pill bottom edge sits ~(inset - 12px) above the viewport bottom,
        // i.e. ~22px above home indicator on iPhone 15 (inset ≈ 34px) — a
        // small gap, not the full 34px floating clear. Floor of 8px keeps a
        // visible gap on devices with no inset (chrome, Android).
        paddingBottom: 'max(8px, calc(var(--mobile-dock-safe-bottom, 0px) - 12px))',
      }}
    >
      {/* Relative wrapper — provides positioning context for the More notch */}
      <div className="relative">

        {/* ── Pill nav — fully rounded capsule ─────────────────── */}
        {/* All four corners rounded (capsule). The pill floats just above
            the iOS home indicator. With viewport-fit=cover, the safe-area
            inset zone below the pill is painted by the page background
            (#000 midnight) — same color as the rest of the screen, so no
            visible "black strip" beyond the natural dark theme. */}
        <nav
          className="flex items-stretch h-[48px]"
          style={{
            borderRadius: 9999,
            background: DOCK_BG,
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            border: `0.5px solid ${DOCK_BORDER}`,
            boxShadow: '0 24px 64px -12px rgba(0,0,0,0.85), 0 4px 16px rgba(0,0,0,0.5)',
            overflow: 'hidden',
          }}
        >
          {NAV_TABS.map(({ label, path, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className="flex-1 flex flex-col items-center justify-center gap-[3px] pb-1.5"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {({ isActive }) => <TabItem icon={Icon} label={label} isActive={isActive} />}
            </NavLink>
          ))}

          {/* Phone — 5th button; opens the softphone panel */}
          <button
            type="button"
            onClick={() => setSoftphoneExpanded(!softphoneExpanded)}
            aria-label={softphoneExpanded ? 'Close phone' : 'Open phone'}
            aria-expanded={softphoneExpanded}
            className="flex-1 flex flex-col items-center justify-center gap-[3px] pb-1.5"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <TabItem icon={Phone} label="Phone" isActive={softphoneExpanded} />
          </button>
        </nav>

        {/* ── More notch — subtle dome ─────────────────────────── */}
        {/* Visual: SVG cubic-bezier arc above Dashboard (center, left: 50%) */}
        <div
          aria-hidden="true"
          className="absolute bottom-full left-1/2 -translate-x-1/2 pointer-events-none"
          style={{ width: HUMP_W, height: HUMP_H }}
        >
          <svg
            width={HUMP_W}
            height={HUMP_H}
            viewBox={`0 0 ${HUMP_W} ${HUMP_H}`}
            style={{ display: 'block' }}
          >
            {/* Glass fill — matches pill background */}
            <path d={HUMP_FILL_D} fill={DOCK_BG} />
            {/* Hairline border on the arc only (pill border closes the bottom edge) */}
            <path
              d={HUMP_BORDER_D}
              fill="none"
              stroke={DOCK_BORDER}
              strokeWidth="0.5"
            />
          </svg>
        </div>

        {/* Tap target.

            This was 96×44 — a fully transparent rectangle, anchored
            `bottom-full` so all 44px of it sat ABOVE the dock, over page
            content, with an 8×8 glyph as its only visible affordance. It
            stole taps from whatever was underneath on every mobile route. A
            browser sweep confirmed it by clicking: on /marketing/social the
            "Posts" tab centre resolved to this button via elementFromPoint,
            so tapping Posts opened the More sheet and the tab could not be
            reached at all. Dashboard tiles, contact rows, an inbox thread, a
            calendar event and a settings input were all measured sitting
            underneath it too.

            Now sized to what it actually shows: MORE_TAP_W wide (the a11y
            minimum) instead of the dome's full 96, and short enough that it
            no longer reaches the row of content immediately above the dock.
            Area over page content drops from 96×38 to 44×22 — about an 84%
            reduction — and the glyph is 16px rather than 8px, so the
            affordance is visible enough to aim at.

            The height is deliberately under the 44px ideal. That is a real
            trade-off and it goes this way round because an invisible target
            that swallows a neighbouring control's taps is the worse failure —
            an unreachable tab is a total loss, a slightly small button is a
            harder tap. The clean answer is a taller, visibly-domed affordance
            whose target matches its paint, but that changes how the dock
            looks, which is Gaige's call rather than a bug fix. */}
        <button
          type="button"
          onClick={() => setMoreMenuOpen(!moreMenuOpen)}
          aria-label="More navigation"
          aria-expanded={moreMenuOpen}
          data-testid="more-nav-button"
          className={cn(
            'absolute bottom-full left-1/2 -translate-x-1/2',
            'flex items-end justify-center pb-[1px]',
            'transition-colors duration-150',
            moreMenuOpen ? 'text-white' : 'text-white/50',
          )}
          style={{
            width: MORE_TAP_W,
            height: MORE_TAP_H,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>

      </div>
    </MobileDockShell>
  )
}
